export const SESSION_STORAGE_KEYS = {
    Error: {
        GlobalRetryFailed: 'global-error-retry-failed',
    },
    Reload: {
        StaleClientHandledCutoff: 'stale-client-reload-handled-cutoff',
    },
} as const

// Custom event name for same-tab sessionStorage changes
const CUSTOM_SESSION_STORAGE_EVENT = 'customSessionStorageChange'

// ============================================
// Standalone Session Storage Functions
// ============================================

/**
 * Get a value from sessionStorage
 */
export function getSessionStorageItem<T>(key: string): T | undefined {
    if (typeof window === 'undefined') return undefined

    try {
        const item = window.sessionStorage.getItem(key)
        return item ? JSON.parse(item) : undefined
    } catch (error) {
        console.error(`Error reading sessionStorage key "${key}":`, error)
        return undefined
    }
}

/**
 * Set a value in sessionStorage and dispatch custom event for same-tab sync
 */
export function setSessionStorageItem<T>(key: string, value: T): void {
    if (typeof window === 'undefined') return

    try {
        window.sessionStorage.setItem(key, JSON.stringify(value))

        // Dispatch custom event for same-tab state updates
        window.dispatchEvent(
            new CustomEvent(CUSTOM_SESSION_STORAGE_EVENT, {
                detail: { key, newValue: value },
            })
        )
    } catch (error) {
        console.error(`Error setting sessionStorage key "${key}":`, error)
    }
}

/**
 * Remove a value from sessionStorage and dispatch custom event for same-tab sync
 */
export function removeSessionStorageItem(key: string): void {
    if (typeof window === 'undefined') return

    try {
        window.sessionStorage.removeItem(key)

        // Dispatch custom event for same-tab state updates
        window.dispatchEvent(
            new CustomEvent(CUSTOM_SESSION_STORAGE_EVENT, {
                detail: { key, newValue: undefined },
            })
        )
    } catch (error) {
        console.error(`Error removing sessionStorage key "${key}":`, error)
    }
}

/**
 * Clear all sessionStorage items
 */
export function clearSessionStorage(): void {
    if (typeof window === 'undefined') return

    try {
        window.sessionStorage.clear()

        // Dispatch custom event for same-tab state updates
        window.dispatchEvent(
            new CustomEvent(CUSTOM_SESSION_STORAGE_EVENT, {
                detail: { key: null, newValue: undefined },
            })
        )
    } catch (error) {
        console.error('Error clearing sessionStorage:', error)
    }
}
